import React, { useEffect, useState, useRef } from 'react'
import { getPackageTypesApi, addPackageTypeApi, deletePackageTypeApi, getPackageDetailsApi, addOrUpdatePackageDetailsApi } from '../../../utils/ApiEndpointFunctions';
import JoditEditor from 'jodit-react';
import toast from 'react-hot-toast';

export default function PackageType() {
  const [showModal, setShowModal] = useState(false);
  const [label, setLabel] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  
  // Package Details state
  const [packageDetailsData, setPackageDetailsData] = useState({});
  const [packageDetailsContent, setPackageDetailsContent] = useState('');
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const editorRef = useRef(null);

  const fetchPackageTypes = async () => {
    try {
      setLoading(true);
      const res = await getPackageTypesApi();
      if (res.status === 200) {
        setData(res.data?.data || []);
      }
    } catch (error) {
      console.error('Error fetching package types:', error);
      toast.error('Failed to load package types');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchPackageTypes();
    fetchPackageDetails();
  }, []);

  const fetchPackageDetails = async () => {
    try {
      setDetailsLoading(true);
      setIsEditingDetails(false);
      const res = await getPackageDetailsApi();
      if (res?.status === 200) {
        // API returns an array; take the first entry if present
        const first = Array.isArray(res.data?.data) ? res.data.data[0] : res.data?.data;
        setPackageDetailsData(first || {});
        setPackageDetailsContent(first?.content || '');
      }
      setDetailsLoading(false);
    } catch (error) {
      console.error('Error fetching package details:', error);
      toast.error('Failed to load package details');
      setDetailsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!label.trim()) {
      toast.error('Please enter package type label');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: label.trim(),
      };
      if (imagePreview) {
        payload.image = imagePreview; // Send base64 string
      }
      const res = await addPackageTypeApi(payload);
      if (res.status === 200 || res.status === 201) {
        toast.success('Added successfully');
        await fetchPackageTypes();
        setLabel('');
        setImagePreview(null);
        setShowModal(false);
      } else {
        toast.error(res.data?.message || 'Something went wrong');
      }
    } catch (error) {
      console.error('Error saving package type:', error);
      toast.error(error?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  }

  const handleSavePackageDetails = async () => {
    if (!packageDetailsContent.trim()) {
      toast.error('Please enter package details');
      return;
    }
    try {
      setDetailsSaving(true);
      const payload = {
        content: packageDetailsContent.trim(),
        id: packageDetailsData?.id || undefined,
      };
      
      console.log('Saving Package Details:', payload);
      const res = await addOrUpdatePackageDetailsApi(payload);
      
      if (res?.status === 200) {
        toast.success(res.data?.message || 'Package details saved successfully');
        setIsEditingDetails(false);
        await fetchPackageDetails();
      } else {
        toast.error(res.data?.message || 'Failed to save package details');
      }
    } catch (error) {
      console.error('Error saving package details:', error);
      toast.error(error?.response?.data?.message || 'Failed to save package details');
    } finally {
      setDetailsSaving(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between flex-wrap align-items-center mt-3 mt-lg-0">
            <h1>Package Types</h1>
            <div style={{ display: '-webkit-inline-box' }}>
              <div className="position-relative my-3">
                                <div className="d-flex gap-2 justify-content-end align-items-center">
                  <button className="btn btn-primary" onClick={() => { setLabel(''); setImagePreview(null); setShowModal(true); }}>Add</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card-body">
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : data.length === 0 ? (
            <p>No package types found.</p>
          ) : (
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Package Type</th>
                  <th>Image</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{index + 1}</td>
                    <td>{item.name || item.label || '-'}</td>
                    <td>
                      {item.image ? (
                        <img src={item.image} alt={item.name} style={{ maxWidth: '80px', maxHeight: '80px', borderRadius: '4px' }} />
                      ) : (
                        '-'
                      )}
                    </td>
                    <td style={{ position: 'relative' }}>
                      <div className="dropdown">
                        <button
                          className="btn  p-0 border-0"
                          type="button"
                          id={`dropdownMenuButton${item.id}`}
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          style={{ fontSize: '1.5rem', lineHeight: 1 }}
                        >
                          <span style={{ cursor: 'pointer' }}>⋮</span>
                        </button>
                        <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton${item.id}`}>
                          <li>
                            <button
                              className="dropdown-item text-danger"
                              onClick={() => setConfirmDeleteId(item.id)}
                            >
                              Delete
                            </button>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Package Details Card */}
      <div className="card" style={{ marginTop: '30px' }}>
        <div className="card-header">
          <h1>Package Details</h1>
        </div>
        <div className="card-body">
          {detailsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div>
              {isEditingDetails ? (
                <div>
                  <JoditEditor
                    ref={editorRef}
                    value={packageDetailsContent}
                    onBlur={(newContent) => {
                      // JoditEditor passes HTML string on blur
                      const cleaned = newContent === '<p><br></p>' ? '' : newContent;
                      setPackageDetailsContent(cleaned);
                    }}
                    onChange={(newContent) => {
                      // keep content in sync as user types
                      const cleaned = newContent === '<p><br></p>' ? '' : newContent;
                      setPackageDetailsContent(cleaned);
                    }}
                  />

                  <div className="mt-3">
                    <button className="btn btn-primary" onClick={handleSavePackageDetails} disabled={detailsSaving}>
                      {detailsSaving ? 'Saving...' : 'Submit'}
                    </button>
                    <button
                      className="btn btn-secondary ms-2"
                      onClick={() => {
                        // cancel edits and reset to fetched content
                        setPackageDetailsContent(packageDetailsData?.content || '');
                        setIsEditingDetails(false);
                      }}
                      disabled={detailsSaving}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {packageDetailsData?.content ? (
                    <div>
                      <div dangerouslySetInnerHTML={{ __html: packageDetailsData?.content }} />
                      <div className="mt-3">
                        <button className="btn btn-primary" onClick={() => setIsEditingDetails(true)}>Edit</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p>No package details found. Click Edit to add content.</p>
                      <button className="btn btn-primary" onClick={() => setIsEditingDetails(true)}>Edit</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal show fade" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Package Type</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Package Type Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Image</label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  {imagePreview && (
                    <div style={{ marginTop: '10px' }}>
                      <img src={imagePreview} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px', borderRadius: '4px' }} />
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setShowModal(false); setLabel(''); setImagePreview(null); }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDeleteId && (
        <div className="modal show fade" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setConfirmDeleteId(null)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this package type?</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={async () => {
                  try {
                    setDeleting(true);
                    const res = await deletePackageTypeApi({ id: confirmDeleteId });
                    if (res.status === 200) {
                      toast.success('Deleted successfully');
                      await fetchPackageTypes();
                      setConfirmDeleteId(null);
                    } else {
                      toast.error(res.data?.message || 'Failed to delete');
                    }
                  } catch (error) {
                    console.error('Error deleting package type:', error);
                    toast.error(error?.response?.data?.message || 'Failed to delete');
                  } finally {
                    setDeleting(false);
                  }
                }} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
